import type { UnifiedReportInput } from './UnifiedReportInput';
import type { AttendanceCardGpaDisplayMode } from './operationsReportFilters';
import type { UnifiedSectionConfig } from './types';

type FieldSection = NonNullable<UnifiedReportInput['fieldSections']>[number];
type FieldSectionField = FieldSection['fields'][number];
type TimeEntry = NonNullable<UnifiedReportInput['timeEntries']>[number];

const GPA_GROUP_SUMMARY_SECTION_KEYS = new Set(['overview', 'client_contact', 'personnel']);

export function hasMeaningfulReportValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export function filterReportFieldSectionFields(
  fieldSection: FieldSection,
  config: UnifiedSectionConfig,
): FieldSectionField[] {
  let fields = fieldSection.fields;

  if (config.showEmptyFields !== true) {
    // 'spacer' fields are deliberate blank lines (CV list-item spacing) —
    // they carry no value by design and must survive the empty-field filter.
    fields = fields.filter((field) => field.type === 'spacer' || hasMeaningfulReportValue(field.value));
  }

  if (fieldSection.sectionKey === 'attendance_timeline') {
    if (config.showTravelDetails === false) {
      fields = fields.filter((field) => !field.key.startsWith('travel_'));
    }
    if (config.showWorkDetails === false) {
      fields = fields.filter((field) => !field.key.startsWith('work_'));
    }
    if (config.showLifecycleMilestones === false) {
      fields = fields.filter((field) => !['started_at', 'completed_at', 'total_work_duration'].includes(field.key));
    }
  }

  const includeFieldKeys = Array.isArray(config.includeFieldKeys)
    ? config.includeFieldKeys.filter((key: unknown): key is string => typeof key === 'string' && key.trim().length > 0)
    : [];
  const excludeFieldKeySet = new Set(
    Array.isArray(config.excludeFieldKeys)
      ? config.excludeFieldKeys.filter((key: unknown): key is string => typeof key === 'string' && key.trim().length > 0)
      : [],
  );

  if (includeFieldKeys.length > 0) {
    const includeSet = new Set(includeFieldKeys);
    fields = fields.filter((field) => includeSet.has(field.key));
  }

  if (excludeFieldKeySet.size > 0) {
    fields = fields.filter((field) => !excludeFieldKeySet.has(field.key));
  }

  return fields;
}

export function filterReportTimeEntries(
  entries: UnifiedReportInput['timeEntries'],
  config: UnifiedSectionConfig,
): TimeEntry[] {
  if (!entries || entries.length === 0) return [];

  if (config.showTravelEntries === false) {
    return entries.filter((entry) => entry.activityType !== 'travel');
  }

  return entries;
}

export function filterGroupedAttendanceCardFieldSections(
  fieldSections: UnifiedReportInput['fieldSections'],
  mode: AttendanceCardGpaDisplayMode,
): FieldSection[] {
  if (!fieldSections || fieldSections.length === 0) {
    return [];
  }

  if (mode !== 'individual_appointments') {
    return fieldSections;
  }

  return fieldSections.filter((section) => GPA_GROUP_SUMMARY_SECTION_KEYS.has(section.sectionKey));
}